import React, { ChangeEvent, useContext } from 'react';

import PropTypes from 'prop-types';

import theme, {
  getInteractiveStyles,
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import RadioGroupContext from './RadioGroupContext';

type RadioButtonProps = Omit<StylableComponent<HTMLDivElement>, 'onChange'> & {
  name?: string;
  checked?: boolean;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string | undefined, e: ChangeEvent) => void;
  inputProps?: StylableComponent<HTMLInputElement>;
  'data-testid'?: string;
};
/**
 * Provides a list of choices (radio buttons) within a form when only one item may be selected at a time.
 */
const RadioButton = React.forwardRef<HTMLInputElement, RadioButtonProps>(
  (
    {
      style,
      className,
      id,
      name: nameProp,
      checked: checkedProp,
      disabled = false,
      value,
      onChange: onChangeProp,
      inputProps = {},
      'data-testid': dataTestid,
      ...props
    },
    ref,
  ) => {
    const radioGroup = useContext(RadioGroupContext);
    let checked = checkedProp;
    let name = nameProp;

    if (radioGroup) {
      if (typeof checked === 'undefined') {
        checked = radioGroup.value === value;
      }
      if (typeof name === 'undefined') {
        name = radioGroup.name;
      }
    }

    const handleChange = (e: ChangeEvent) => {
      if (onChangeProp) {
        onChangeProp(value, e);
      } else if (radioGroup) {
        radioGroup.onChange?.(value, e);
      }
    };

    const { style: inputStyle, ...rest } = inputProps;

    return (
      <div
        className={className}
        css={[
          {
            display: 'block',
            position: 'relative',
            height: '16px',
            width: '16px',
            borderWidth: checked ? '0px' : '1px',
            borderStyle: 'solid',
            borderColor: disabled
              ? theme.tokens.colors.border.inactive.value
              : theme.tokens.colors.border.value,
            borderRadius: theme.borderRadius.circle,
            cursor: 'pointer',
            ...getInteractiveStyles(disabled),
          },
          style,
        ]}
        {...props}
      >
        {checked && (
          <div
            css={[
              {
                borderRadius: theme.borderRadius.circle,
                width: '100%',
                height: '100%',
                boxShadow: `inset 0 0 0 4px ${
                  disabled
                    ? theme.tokens.colors.background.neutral.inactive.value
                    : theme.tokens.colors.background.selected.value
                }`,
              },
            ]}
          />
        )}
        <input
          ref={ref}
          id={id}
          name={name}
          type="radio"
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={disabled ? undefined : handleChange}
          data-testid={dataTestid}
          css={[
            {
              cursor: 'inherit',
              position: 'absolute',
              opacity: 0,
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              margin: 0,
              padding: 0,
              zIndex: 1,
              '&:before': {
                content: "' '",
                position: 'absolute',
                width: 20,
                height: 20,
                top: checked ? '-2px' : '-3px',
                left: checked ? '-2px' : '-3px',
              },
              // pointer-events needs to be 'none' when disabled for a wrapping
              // Tooltip to disappear correctly: https://github.com/react-component/tooltip/issues/18#issuecomment-411476678
              pointerEvents: disabled ? 'none' : 'auto',
            },
            inputStyle,
          ]}
          {...rest}
        />
      </div>
    );
  },
);

RadioButton.displayName = 'RadioButton';

RadioButton.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The id of the radio, used for the label */
  id: PropTypes.string,
  /** The name for the radio input */
  name: PropTypes.string,
  /** The value of the radio */
  checked: PropTypes.bool,
  /** The value of the radio */
  value: PropTypes.any,
  /** onChange handler for the radio input */
  onChange: PropTypes.func,
  /** Disable radio functionality */
  disabled: PropTypes.bool,
  /** Props passed to the input element */
  inputProps: PropTypes.object,
  /** data-testid passed to the input element */
  'data-testid': PropTypes.string,
};

export default RadioButton;
